import { ErrorHandler } from '@frontend/error-handler';
import { CommonMessage } from '@frontend/lang';
import { ProductClient } from '@frontend/product/api';
import { IProduct, ProductType } from '@frontend/product/types';
import { Product } from '@frontend/product/utils';
import { useBlobRepository } from '@frontend/repository';
import { ReactNode, useEffect, useMemo, useState } from 'react';

import ImagePlaceholder from '../../../../../assets/images/image-placeholder.png';
import { useAppSelector } from '../../../../redux/store';
import { ProductCardProps } from './product-card.component';

interface ProductAttributeDisplay {
    attributeName: ReactNode;
    valueName: string;
}
export const useProductCard = (props: ProductCardProps) => {
    const blobRepository = useBlobRepository();
    const navigationStore = useAppSelector((state) => state.navigation);
    const [product, setProduct] = useState<IProduct | undefined>(props.product);
    const [image, setImage] = useState<string | null>(null);
    const [productAttributes, setProductAttributes] = useState<ProductAttributeDisplay[]>([]);

    const { isDisabled, disabledReason } = useMemo<{ isDisabled: boolean; disabledReason: React.ReactNode | null }>(() => {
        // Check allowance
        if (product?.type !== ProductType.KIT && props.allowance !== undefined && !props.allowance?.allowed) {
            return { isDisabled: true, disabledReason: CommonMessage.ERRORS.NOT_IN_BUDGET };
        }
        if (product?.type === ProductType.EQUIPMENT && props.certificationLoading) {
            return { isDisabled: true, disabledReason: null };
        }
        if (product?.type === ProductType.EQUIPMENT && props.certification !== undefined && !props.certification?.certified) {
            return { isDisabled: true, disabledReason: CommonMessage.ERRORS.NOT_CERTIFIED };
        }
        // Check stock - show different message if available in other spots
        if (props.stock !== undefined && props.stock !== null && !props.stock.available) {
            if (props.hasStockInOtherSpots) {
                return { isDisabled: true, disabledReason: CommonMessage.OBJECTS.STOCK.IN_OTHER_SPOT };
            }
            return { isDisabled: true, disabledReason: CommonMessage.ERRORS.OUT_OF_STOCK };
        }
        return { isDisabled: false, disabledReason: null };
    }, [product?.type, props.allowance, props.certification, props.certificationLoading, props.stock, props.hasStockInOtherSpots]);

    useEffect(() => {
        if (!navigationStore.iot) return;
        if (props.product_id && !props.product && !product) {
            ProductClient.fetchProduct(navigationStore.iot.account_id, props.product_id).then(setProduct);
        }
    }, [props.product_id, props.product, navigationStore.iot]);

    useEffect(() => {
        if (!product) return;
        new Product(product)
            .getBlobImage(blobRepository)
            .then((image) => {
                if (!image) setImage(ImagePlaceholder);
                else setImage(image);
            })
            .catch(ErrorHandler.handleError);
    }, [product]);

    useEffect(() => {
        if (!product || (props.variant !== 'attributes' && props.variant !== 'stock')) return;

        if (product.attributes) {
            const displays: ProductAttributeDisplay[] = Object.entries(product.attributes).map(([name, value]) => ({
                attributeName: name,
                valueName: value
            }));
            setProductAttributes(displays);
        } else {
            setProductAttributes([]);
        }
    }, [product, props.variant]);

    return {
        product,
        image,
        productAttributes,
        placeholder: ImagePlaceholder,
        isDisabled,
        disabledReason
    };
};
